import { useEffect, useState } from "react";
import { Search, Ban, RotateCw, ShieldCheck, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { toast } from "sonner";

const fmt = (n) => "$" + Number(n || 0).toLocaleString();

const AdminUsers = () => {
  const { api, admin } = useAdminAuth();
  const [tab, setTab] = useState("creator");
  const [query, setQuery] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);   // {kind, user}
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const isSuper = admin?.role === "super_admin";

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/users", { params: { role: tab, q: query || undefined } }); setUsers(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const suspend = async () => {
    if (!reason.trim()) return toast.error("Reason required");
    setBusy(true);
    try {
      await api.post(`/admin/users/${modal.user.user_id}/${modal.kind}`, { reason });
      toast.success(modal.kind === "suspend" ? "Account suspended" : "Account reactivated");
      setModal(null); setReason(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const toggleKyc = async (u) => {
    setBusy(true);
    try {
      await api.post(`/admin/users/${u.user_id}/kyc-verify`, { verified: !u.kyc_verified });
      toast.success(!u.kyc_verified ? "KYC verified" : "KYC unverified");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="admin-users-page">
      <div>
        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E85D04]">Users</span>
        <h1 className="mt-1 font-display text-3xl md:text-4xl font-semibold tracking-tight">User management</h1>
        <p className="mt-1 text-sm text-[#0D1B2A]/60">Manage creators, backers, KYC, and account status.</p>
      </div>

      <div className="mt-6 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
        <div className="flex gap-2">
          {[["creator", "Creators"], ["backer", "Backers"]].map(([v, l]) => (
            <button key={v} onClick={() => setTab(v)} data-testid={`users-tab-${v}`}
              className={`px-4 py-2 rounded-xl text-sm font-medium border transition-colors ${tab === v ? "bg-[#0D1B2A] text-white border-[#0D1B2A]" : "bg-white text-[#0D1B2A]/80 border-[#0D1B2A]/15 hover:bg-[#0D1B2A]/5"}`}>{l}</button>
          ))}
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#0D1B2A]/40" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Search by name or email…" data-testid="users-search"
              className="pl-9 h-10 w-64 rounded-xl border-[#0D1B2A]/15" />
          </div>
          <button onClick={load} className="rounded-xl px-3 h-10 text-sm border border-[#0D1B2A]/15 hover:bg-[#0D1B2A]/5">Search</button>
        </div>
      </div>

      <div className="mt-6 rounded-2xl bg-white border border-[#0D1B2A]/10 overflow-hidden">
        {loading ? (
          <div className="p-12 grid place-items-center text-[#0D1B2A]/60"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : users.length === 0 ? (
          <div className="p-12 text-center text-[#0D1B2A]/60">No users match.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FFFBF5] text-[#0D1B2A]/50 text-xs uppercase tracking-widest">
                <tr>
                  <th className="text-left px-5 py-3">Name</th>
                  <th className="text-left px-5 py-3">Email</th>
                  {tab === "creator" && <th className="text-left px-5 py-3">Phone</th>}
                  {tab === "creator" ? (<>
                    <th className="text-right px-5 py-3">Campaigns</th>
                    <th className="text-right px-5 py-3">Total raised</th>
                    <th className="text-left px-5 py-3">KYC</th>
                  </>) : (<>
                    <th className="text-right px-5 py-3">Backed</th>
                    <th className="text-right px-5 py-3">Total pledged</th>
                  </>)}
                  <th className="text-left px-5 py-3">Status</th>
                  <th className="text-right px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#0D1B2A]/5">
                {users.map((u) => (
                  <tr key={u.user_id} data-testid={`user-row-${u.user_id}`} className="hover:bg-[#FFFBF5]/60">
                    <td className="px-5 py-3 font-medium">{u.name || "—"}</td>
                    <td className="px-5 py-3 text-[#0D1B2A]/80">{u.email}</td>
                    {tab === "creator" && <td className="px-5 py-3 text-[#0D1B2A]/80">{u.phone || "—"}</td>}
                    {tab === "creator" ? (<>
                      <td className="px-5 py-3 text-right">{u.campaigns_count}</td>
                      <td className="px-5 py-3 text-right font-medium">{fmt(u.total_raised)}</td>
                      <td className="px-5 py-3">
                        <button onClick={() => toggleKyc(u)} data-testid={`kyc-toggle-${u.user_id}`} disabled={busy}
                          className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-semibold border ${u.kyc_verified ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-white border-[#0D1B2A]/15 text-[#0D1B2A]/70"}`}>
                          <ShieldCheck className="h-3.5 w-3.5" /> {u.kyc_verified ? "Verified" : "Not verified"}
                        </button>
                      </td>
                    </>) : (<>
                      <td className="px-5 py-3 text-right">{u.backed_count}</td>
                      <td className="px-5 py-3 text-right font-medium">{fmt(u.total_backed)}</td>
                    </>)}
                    <td className="px-5 py-3">
                      <span className={`text-xs rounded-full px-2 py-0.5 ${u.status === "suspended" ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>{u.status || "active"}</span>
                    </td>
                    <td className="px-5 py-3 text-right">
                      {u.status === "suspended" ? (
                        <button disabled={!isSuper} onClick={() => { setModal({ kind: "reactivate", user: u }); setReason(""); }}
                          data-testid={`reactivate-${u.user_id}`}
                          className="inline-flex items-center gap-1 rounded-lg border border-[#0D1B2A]/15 hover:bg-[#0D1B2A]/5 px-3 py-1.5 text-xs font-semibold disabled:opacity-40">
                          <RotateCw className="h-3.5 w-3.5" /> Reactivate
                        </button>
                      ) : (
                        <button disabled={!isSuper} onClick={() => { setModal({ kind: "suspend", user: u }); setReason(""); }}
                          data-testid={`suspend-${u.user_id}`}
                          className="inline-flex items-center gap-1 rounded-lg bg-red-600 text-white hover:bg-red-700 px-3 py-1.5 text-xs font-semibold disabled:opacity-40">
                          <Ban className="h-3.5 w-3.5" /> Suspend
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!modal} onOpenChange={(o) => { if (!o) { setModal(null); setReason(""); } }}>
        <DialogContent className="sm:max-w-[480px] rounded-2xl" data-testid="user-action-modal">
          <DialogHeader><DialogTitle className="font-display">{modal?.kind === "suspend" ? "Suspend account" : "Reactivate account"}</DialogTitle></DialogHeader>
          <p className="text-sm text-[#0D1B2A]/70">User: <strong>{modal?.user?.name}</strong> ({modal?.user?.email})</p>
          <Textarea rows={4} value={reason} onChange={(e) => setReason(e.target.value)}
            data-testid="user-action-reason"
            placeholder={modal?.kind === "suspend" ? "Why is this account being suspended?" : "Why is this account being reactivated?"}
            className="rounded-xl border-[#0D1B2A]/15" />
          <div className="flex justify-end gap-2">
            <button onClick={() => { setModal(null); setReason(""); }} className="rounded-xl px-4 py-2 text-sm border border-[#0D1B2A]/15">Cancel</button>
            <button onClick={suspend} disabled={busy || !reason.trim()}
              data-testid="user-action-submit"
              className={`rounded-xl text-white px-4 py-2 text-sm font-semibold disabled:opacity-60 ${modal?.kind === "suspend" ? "bg-red-600 hover:bg-red-700" : "bg-[#E85D04] hover:bg-[#c94d02]"}`}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsers;
